import { useAddNativeClasses } from '@carvana/merch-native/hooks';
import dynamic from 'next/dynamic';
import { useContext } from 'react';
import useDisplayTradeAppliedToast from '~/components/common/TradeAppliedToastContent/useDisplayTradeAppliedToast';
import { heliosSizes } from '~/constants/common/resizeBreakpoints';
import { useNativeDeviceType } from '~/context/common/DeviceContext';
import { useIsAppRouter } from '~/context/common/StaticContext';
import TradeContext from '~/context/common/TradeContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import ModalContext from '~/context/vdp/ModalContext';
import useToggleAccountModal from '~/hooks/common/accountModal/useToggleAccountModal';
import useGeolocation from '~/hooks/common/geolocation/useGeolocation';
import useGetTrades from '~/hooks/common/services/useGetTrades';
import useSyncGeolocation from '~/hooks/common/sync/useSyncGeolocation';
import useSynchronizeAuth from '~/hooks/common/sync/useSynchronizeAuth';
import useToggleAppliedTradeCookie from '~/hooks/common/sync/useToggleAppliedTradeCookie';
import useDataDogRUM from '~/hooks/common/telemetry/useDataDogRUM';
import useSetTradeIncentiveDDContext from '~/hooks/common/telemetry/useSetTradeIncentiveDDContext';
import useTrackBrowserCookieIdMismatch from '~/hooks/common/telemetry/useTrackBrowserCookieIdMismatch';
import { useTrackClickEvents } from '~/hooks/common/telemetry/useTrackClickEvents';
import useTrackServerClientMobileMismatch from '~/hooks/common/telemetry/useTrackServerClientMobileMismatch';
import useTrackUserGeolocations from '~/hooks/common/telemetry/useTrackUserGeolocations';
import useModalScrollRestoration from '~/hooks/common/ui/useModalScrollRestoration';
import useResizeListener from '~/hooks/common/ui/useResizeListener';
import useSebastianLoaded from '~/hooks/common/useSebastianLoaded';
import { useGetFavorites } from '~/hooks/vdp/favorites';
import useGetDeliveryPickup from '~/hooks/vdp/services/useGetDeliveryPickup';
import useGetDiscounts from '~/hooks/vdp/services/useGetDiscounts';
import useGetReviews from '~/hooks/vdp/services/useGetReviews';
import useGetSebastianQuestions from '~/hooks/vdp/services/useGetSebastianQuestions';
import useGetUserPurchasing from '~/hooks/vdp/services/useGetUserPurchasing';
import useGetVehicleAvailability from '~/hooks/vdp/services/useGetVehicleAvailability';
import useGetVehiclePricing from '~/hooks/vdp/services/useGetVehiclePricing';
import useGetWhyBuyData from '~/hooks/vdp/services/useGetWhyBuyData';
import useTrackPrimaryCtaRenderPerformance from '~/hooks/vdp/telemtry/useTrackPrimaryCtaRenderPerformance';
import useTrackVdpCallLoad from '~/hooks/vdp/telemtry/useTrackVdpCallLoad';
import useAddMetaAttributes from '~/hooks/vdp/useAddMetaAttributes';
import useHeroViewedEvent from '~/hooks/vdp/useHeroViewedEvent';
import useLocalStorageVehicleSubscription from '~/hooks/vdp/useLocalStorageVehicleSubscription';
import useRemoveQueryParams from '~/hooks/vdp/useRemoveQueryParams';
import useSendTradeIncentiveEvents from '~/hooks/vdp/useSendTradeIncentiveEvents';
import useSendVdpSebastianPageContext from '~/hooks/vdp/useSendVdpSebastianPageContext';
import useSetAvailabilityDataDogContext from '~/hooks/vdp/useSetAvailabilityDataDogContext';
import useSetHasVisitedVDP from '~/hooks/vdp/useSetHasVisitedVDP';
import {
  useNativeMessageDispatcher,
  useNativeMessageListener
} from '~/native/vdp';
import {
  usePageHeaderLoaded,
  useScrollToParam,
  useSendDataEvents
} from './hooks';
import useSendSoftNotify from './hooks/events/useSendSoftNotify';

const PagesRouterDataHooks = dynamic(
  () =>
    import(
      /* webpackChunkName: "PagesRouterDataHooks" */ './PagesRouterDataHooks'
    )
);

const AppRouterDataHooks = dynamic(
  () =>
    import(/* webpackChunkName: "AppRouterDataHooks" */ './AppRouterDataHooks')
);

export default function VehicleDetailsData() {
  const isAppRouter = useIsAppRouter();
  const { isAccountModalOpen, isAnyModalOpen, setAccountModalOpen } =
    useContext(ModalContext);
  const { preferredAcquisitionNameFromQueryParam } = useContext(TradeContext);
  const isLoggedIn = useIsLoggedIn();
  const nativeDeviceType = useNativeDeviceType();
  useDataDogRUM();
  useHeroViewedEvent();
  useSynchronizeAuth();
  useScrollToParam();
  usePageHeaderLoaded();
  useSebastianLoaded();
  useSendVdpSebastianPageContext();
  useSendDataEvents();
  useSendSoftNotify();
  useTrackClickEvents();
  useTrackVdpCallLoad();
  useTrackPrimaryCtaRenderPerformance();
  useResizeListener(heliosSizes);

  useNativeMessageDispatcher();
  useNativeMessageListener();
  useAddNativeClasses(nativeDeviceType.isNative, nativeDeviceType.platform);

  useGetDiscounts();
  useGeolocation();
  useModalScrollRestoration(isAnyModalOpen);
  useGetFavorites();
  useGetTrades(isLoggedIn, preferredAcquisitionNameFromQueryParam);
  useDisplayTradeAppliedToast();
  useGetDeliveryPickup();
  useGetVehicleAvailability();
  useGetUserPurchasing();
  useGetVehiclePricing();
  useGetWhyBuyData();
  useGetReviews();
  useRemoveQueryParams();
  useToggleAppliedTradeCookie();
  useLocalStorageVehicleSubscription();
  useToggleAccountModal({ isAccountModalOpen, setAccountModalOpen });
  useTrackBrowserCookieIdMismatch();
  useTrackServerClientMobileMismatch();
  useGetSebastianQuestions();
  useSyncGeolocation();
  useSendTradeIncentiveEvents();
  useSetTradeIncentiveDDContext();
  useSetHasVisitedVDP();
  useSetAvailabilityDataDogContext();
  useTrackUserGeolocations();
  useAddMetaAttributes();

  return isAppRouter ? <AppRouterDataHooks /> : <PagesRouterDataHooks />;
}
